import { Color, Spacing } from '@persona-id/design-tokens';
import { Fragment, ReactElement } from 'react';
import styled from 'styled-components';

import GraphHoverCardEmailSimilarity from 'dashboard/components/GraphHoverCard/GraphHoverCardEmailSimilarity';
import GraphHoverCardImageBackgroundSimilarity from 'dashboard/components/GraphHoverCard/GraphHoverCardImageBackgroundSimilarity';
import GraphHoverCardTransactions from 'dashboard/components/GraphHoverCard/GraphHoverCardTransactions';
import GraphHoverCardAccount from 'dashboard/components/GraphHoverCardAccount';
import GraphHoverCardAccountSimilaritySelfie from 'dashboard/components/GraphHoverCardAccountSimilaritySelfie';
import GraphHoverCardAccountSimilaritySelfieCollapsed from 'dashboard/components/GraphHoverCardAccountSimilaritySelfieCollapsed';
import GraphHoverCardProperty from 'dashboard/components/GraphHoverCardProperty';
import { LoadingShimmerParagraph } from 'dashboard/components/LoadingShimmer/LoadingShimmer';
import {
  CollapsedPairwiseNode,
  DatumTypeToken,
  DatumValueType,
  Node,
  NodeRenderConfig,
} from 'dashboard/models/graph';
import { HydratedAccountSimilaritySelfie, HydratedNode } from 'dashboard/models/graph-hydration';
import { BASE } from 'design-system/variables/spacing';

const ContentBody = styled('div')`
  min-width: 240px;
  max-width: 400px;
  max-height: 350px;
  overflow: auto;
  padding: ${Spacing[4]};
`;

const FIRST_CARD_NUM_ROWS = 2;
const FirstCard = styled.div`
  margin: ${Spacing[2]};
  display: grid;
  grid-template-columns: 85%;
  grid-template-rows: repeat(5, auto);
  grid-column-gap: ${BASE * 10}px;
  grid-row-gap: ${Spacing[4]};
`;

const ActionDrawer = styled('div')`
  bottom: 0;
  display: fixed;
  border-top: solid ${Color.CoreSlate200} 1px;
  padding: ${Spacing[4]};
`;

const ActionToolbar = styled('div')`
  display: flex;
  flex-direction: row;
`;

const MenuContents = ({
  node,
  collapsedPairwiseNode,
  hydratedNodes,
  getNodeRenderConfig,
}: {
  node: Node;
  collapsedPairwiseNode: CollapsedPairwiseNode;
  hydratedNodes: HydratedNode[];
  getNodeRenderConfig: (token: DatumTypeToken) => NodeRenderConfig;
}) => {
  switch (hydratedNodes[0].nodeType) {
    case DatumValueType.Account:
      return (
        <GraphHoverCardAccount
          hydratedAccount={hydratedNodes[0]}
          getNodeRenderConfig={getNodeRenderConfig}
        />
      );
    case DatumValueType.AccountSimilaritySelfie:
      if (collapsedPairwiseNode) {
        // Currently collapsedPairwiseNode can only be of case similarity selfies
        return (
          <GraphHoverCardAccountSimilaritySelfieCollapsed
            hydratedAccountSimilaritySelfies={hydratedNodes as HydratedAccountSimilaritySelfie[]}
          />
        );
      }
      return (
        <GraphHoverCardAccountSimilaritySelfie hydratedAccountSimilaritySelfie={hydratedNodes[0]} />
      );
    case DatumValueType.EmailSimilarity:
      return <GraphHoverCardEmailSimilarity hydratedEmailSimilarity={hydratedNodes[0]} />;
    case DatumValueType.ImageBackgroundSimilarity:
      return (
        <GraphHoverCardImageBackgroundSimilarity
          hydratedImageBackgroundSimilarity={hydratedNodes[0]}
        />
      );
    case DatumValueType.Transactions:
      return <GraphHoverCardTransactions hydratedTransactions={hydratedNodes[0]} />;
    default:
      return (
        <GraphHoverCardProperty
          nodeId={node.id}
          hydratedNode={hydratedNodes[0]}
          getNodeRenderConfig={getNodeRenderConfig}
        />
      );
  }
};

interface GraphNodeHoverCardProps {
  node: Node;
  collapsedPairwiseNode: CollapsedPairwiseNode;
  hydratedNodes?: HydratedNode[];
  getNodeRenderConfig: (token: DatumTypeToken) => NodeRenderConfig;
  actions?: (node: Node) => ReactElement[];
}

const GraphNodeHoverCard = ({
  node,
  collapsedPairwiseNode,
  hydratedNodes,
  getNodeRenderConfig,
  actions,
}: GraphNodeHoverCardProps) => {
  const isLoading = hydratedNodes == null;
  const actionsButtons = actions && node ? actions(node) : null;

  return (
    <div>
      <ContentBody>
        {isLoading ? (
          <FirstCard>
            {[...Array(FIRST_CARD_NUM_ROWS)].map((_, i) => {
              return (
                <Fragment key={i}>
                  <LoadingShimmerParagraph size='md' width={`${i % 2 === 0 ? '100%' : '80%'}`} />
                  <LoadingShimmerParagraph size='md' width={`${i % 2 === 0 ? '100%' : '80%'}`} />
                </Fragment>
              );
            })}
          </FirstCard>
        ) : (
          <MenuContents
            node={node}
            collapsedPairwiseNode={collapsedPairwiseNode}
            hydratedNodes={hydratedNodes}
            getNodeRenderConfig={getNodeRenderConfig}
          />
        )}
      </ContentBody>
      {actionsButtons && actionsButtons.length > 0 && (
        <ActionDrawer>
          <ActionToolbar>{actionsButtons}</ActionToolbar>
        </ActionDrawer>
      )}
    </div>
  );
};

export default GraphNodeHoverCard;
